import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, MapPin, ChevronDown, User } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { fetchCities } from '../lib/api';
import { useSiteSettings } from '../lib/SiteSettingsContext';
import { BRAND_LOGO_URL, BRAND_NAME } from '../lib/brand';

const navLinks = [
    { label: 'Buy', to: '/buy' },
    { label: 'Rent', to: '/rent' },
    { label: 'New Projects', to: '/new-projects' },
    { label: 'PG / Co-living', to: '/pg' },
    { label: 'Plots', to: '/plots' },
    { label: 'Blog', to: '/blog' },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeCity, setActiveCity] = useState(localStorage.getItem('city') || 'Mumbai');
    const [cities, setCities] = useState([]);
    const [cityDropdown, setCityDropdown] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { settings } = useSiteSettings();
    const brand = settings.site_name || BRAND_NAME;
    const logoUrl = settings.site_logo_url || BRAND_LOGO_URL;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        fetchCities(true)
            .then(arr => setCities(Array.isArray(arr) ? arr : []))
            .catch(() => setCities([]));
    }, []);

    const isHome = location.pathname === '/';
    const navOnDark = isHome && !scrolled;

    const headerBase = scrolled || !isHome
        ? 'bg-white border-b border-[#E6E4DF] shadow-[0_1px_0_#E6E4DF]'
        : 'bg-white lg:bg-transparent border-b border-[#E6E4DF] lg:border-0';

    const linkColor = navOnDark ? 'text-[#48453F] hover:text-[#1B4332] lg:text-white/90 lg:hover:text-white' : 'text-[#48453F] hover:text-[#1B4332]';

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${headerBase}`}
            data-testid="navbar"
        >
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-[84px] flex items-center justify-between">
                {/* Left: logo */}
                <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo" aria-label={`${brand} home`}>
                    <img
                        src={logoUrl}
                        alt={`${brand} logo`}
                        width="200"
                        height="72"
                        className="h-16 sm:h-[72px] w-auto rounded-md object-contain"
                        loading="eager"
                    />
                </Link>

                {/* Center: nav */}
                <nav className="hidden lg:flex items-center gap-7">
                    {navLinks.map(l => {
                        const active = location.pathname === l.to;
                        return (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|\//g, '-')}`}
                                className={`text-[13.5px] font-medium tracking-tight ${
                                    active ? 'text-[#E07B39]' : linkColor
                                } transition-colors`}
                            >
                                {l.label}
                            </Link>
                        );
                    })}
                </nav>

                {/* Right */}
                <div className="hidden lg:flex items-center gap-3">
                    {/* City selector */}
                    <div className="relative">
                        <button
                            onClick={() => setCityDropdown(v => !v)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-medium border transition min-h-[36px] ${
                                navOnDark
                                    ? 'text-white border-white/30 hover:bg-white/10'
                                    : 'text-[#1B4332] border-[#E6E4DF] hover:bg-[#F4F3EF]'
                            }`}
                            data-testid="city-selector-btn"
                            aria-label={`Selected city: ${activeCity}. Change city`}
                            aria-haspopup="menu"
                            aria-expanded={cityDropdown}
                        >
                            <MapPin size={14} className="text-[#E07B39]" aria-hidden="true" />
                            {activeCity}
                            <ChevronDown size={14} aria-hidden="true" />
                        </button>
                        {cityDropdown && (
                            <div className="absolute right-0 mt-2 w-[460px] bg-white rounded-2xl shadow-[0_8px_40px_rgba(27,67,50,0.16)] border border-[#E6E4DF] p-4 z-50" data-testid="city-dropdown">
                                <div className="text-[11px] font-bold tracking-[0.12em] text-[#87847E] uppercase mb-3 px-2">Choose your city</div>
                                <div className="grid grid-cols-3 gap-2">
                                    {cities.map(c => (
                                        <button
                                            key={c.slug}
                                            onClick={() => {
                                                setActiveCity(c.name);
                                                localStorage.setItem('city', c.name);
                                                setCityDropdown(false);
                                                navigate(`/city/${c.slug}`);
                                            }}
                                            className={`p-2 rounded-xl flex items-center gap-2 hover:bg-[#F4F3EF] text-left transition ${
                                                activeCity === c.name ? 'bg-[#F4F3EF]' : ''
                                            }`}
                                            data-testid={`city-option-${c.slug}`}
                                        >
                                            <img src={c.image_url} alt={c.name} className="w-9 h-9 rounded-lg object-cover" />
                                            <div>
                                                <div className="text-[13px] font-semibold text-[#18160E]">{c.name}</div>
                                                <div className="text-[10px] text-[#87847E]">{c.property_count} properties</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <Link
                        to="/saved"
                        className={`relative p-2.5 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center ${navOnDark ? 'text-white hover:bg-white/10' : 'text-[#1B4332] hover:bg-[#F4F3EF]'}`}
                        data-testid="saved-btn"
                        aria-label="View saved properties"
                    >
                        <Heart size={18} aria-hidden="true" />
                    </Link>

                    <Link
                        to="/login"
                        className={`p-2.5 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center ${navOnDark ? 'text-white hover:bg-white/10' : 'text-[#1B4332] hover:bg-[#F4F3EF]'}`}
                        data-testid="user-btn"
                        aria-label="Sign in or register"
                    >
                        <User size={18} aria-hidden="true" />
                    </Link>
                    <Link
                        to="/register"
                        className="hidden xl:inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-white text-[13px] font-semibold transition-opacity hover:opacity-90"
                        style={{ backgroundColor: '#E07B39' }}
                        data-testid="list-property-cta"
                    >
                        Post Property FREE
                    </Link>
                </div>

                {/* Mobile */}
                <div className="lg:hidden flex items-center gap-2">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                className={`p-2.5 rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center ${navOnDark ? 'text-[#1B4332] lg:text-white' : 'text-[#1B4332]'}`}
                                data-testid="mobile-menu-btn"
                                aria-label="Open navigation menu"
                            >
                                <Menu size={22} aria-hidden="true" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-[#1B4332] border-l-0 text-white w-[300px] [&>button]:text-white [&>button]:opacity-100 [&>button]:rounded-full [&>button]:p-2 [&>button]:hover:bg-white/15 [&>button>svg]:h-5 [&>button>svg]:w-5">
                            <div className="flex flex-col gap-4 pt-8" data-testid="mobile-menu-content">
                                <img src={logoUrl} alt={`${brand} logo`} width="200" height="64" className="h-16 w-auto rounded-md object-contain self-start bg-black/30 p-1" />
                                {navLinks.map(l => (
                                    <Link
                                        key={l.to}
                                        to={l.to}
                                        onClick={() => setOpen(false)}
                                        className="text-white/90 hover:text-[#E07B39] text-[17px] py-2"
                                        data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s|\//g, '-')}`}
                                    >
                                        {l.label}
                                    </Link>
                                ))}
                                <Link
                                    to="/tools"
                                    onClick={() => setOpen(false)}
                                    className="text-white/90 hover:text-[#E07B39] text-[17px] py-2"
                                >Tools</Link>
                                <Link
                                    to="/about-us"
                                    onClick={() => setOpen(false)}
                                    className="text-white/90 hover:text-[#E07B39] text-[17px] py-2"
                                >About</Link>
                                <Link
                                    to="/contact"
                                    onClick={() => setOpen(false)}
                                    className="text-white/90 hover:text-[#E07B39] text-[17px] py-2"
                                >Contact</Link>
                                <Link
                                    to="/buy"
                                    onClick={() => setOpen(false)}
                                    className="mt-4 btn-saffron text-center px-5 py-3"
                                >Post Property FREE</Link>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
